import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Checkbox,
  Tabs,
  Tab,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import {
  getProjectActivities,
  getScopeBillingMilestones,
} from "../../../../services/scheduleService";
import { updatePaymentGroup } from "../../../../services/paymentGroupService";
import { toast } from "react-toastify";
import moment from "moment";
import { getStatusDisplayText } from "./ActivitiesList";
import { formattedAmountWithoutAed } from "../../../../utils/CommonFunction";
import { BsFillDiamondFill } from "react-icons/bs";
import { ReactComponent as SmallDhiramIcon } from "../../../../assets/svg/SmallDhiramIcon.svg";
import { ReactComponent as NewDesignIcon } from "../../../../assets/svg/NewDesignIcon.svg";
import { ReactComponent as NewRenovationIcon } from "../../../../assets/svg/NewRenovationIcon.svg";
import { ReactComponent as LeftArrowIcon } from "../../../../assets/svg/LeftArrowIcon.svg";
import { ReactComponent as RightArrowIcon } from "../../../../assets/svg/RightArrowIcon.svg";
import { milestoneStatusStyleArray } from "../../config";
import styles from "./ActivitiesDetailsStyles";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "none",
  textTransform: "capitalize",
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  color: "#949DAA",
  backgroundColor: "#fff",
  border: "1px solid #d7d8d9",
  borderRadius: "10px",
  minHeight: "40px !important",
  fontWeight: 500,
  textTransform: "none",
  marginRight: "10px",
  padding: "4px 16px !important",
  fontFamily: "Inter Tight",
  "&.Mui-selected": {
    color: "#fff !important",
    backgroundColor: "#0d0d0d !important",
  },
  "&:hover": {
    color: "#000",
  },
  "&.Mui-disabled": {
    opacity: 0.7,
    cursor: "not-allowed !important",
    backgroundColor: "#f5f5f5 !important",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "transparent",
  },
}));

const ActivitiesDetails = () => {
  const { id, scopeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const componentStyles = styles(isMobile);

  // Route :id is proposal_id. Activities APIs need the project id when provided.
  const projectIdForApi = searchParams.get("project_id") || id;

  const [activeTab, setActiveTab] = useState(0);
  const [selectedActivities, setSelectedActivities] = useState(new Set());
  const [multipleDragEnabled, setMultipleDragEnabled] = useState(true);

  const [activitiesData, setActivitiesData] = useState(null);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState(null);

  const [billingMilestonesData, setBillingMilestonesData] = useState(null);
  const [billingMilestonesLoading, setBillingMilestonesLoading] =
    useState(true);
  const [billingMilestonesError, setBillingMilestonesError] = useState(null);

  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch project activities
  useEffect(() => {
    if (!projectIdForApi) {
      setActivitiesData({ data: [] });
      setActivitiesLoading(false);
      return;
    }

    const fetchActivities = async () => {
      try {
        setActivitiesLoading(true);
        setActivitiesError(null);
        const response = await getProjectActivities(projectIdForApi);
        if (response.success) {
          setActivitiesData(response);
        } else {
          setActivitiesError(
            new Error(response.message || "Failed to fetch activities")
          );
        }
      } catch (err) {
        setActivitiesError(err);
        setActivitiesData(null);
      } finally {
        setActivitiesLoading(false);
      }
    };

    fetchActivities();
  }, [projectIdForApi]);

  // Fetch billing milestones
  useEffect(() => {
    if (!scopeId) {
      setBillingMilestonesData(null);
      setBillingMilestonesLoading(false);
      return;
    }

    const fetchMilestones = async () => {
      try {
        setBillingMilestonesLoading(true);
        setBillingMilestonesError(null);
        const response = await getScopeBillingMilestones(scopeId);
        if (response.success) {
          setBillingMilestonesData(response);
        } else {
          setBillingMilestonesError(
            new Error(response.message || "Failed to fetch billing milestones")
          );
        }
      } catch (err) {
        setBillingMilestonesError(err);
        setBillingMilestonesData(null);
      } finally {
        setBillingMilestonesLoading(false);
      }
    };

    fetchMilestones();
  }, [scopeId]);

  const refetchBillingMilestones = () => {
    if (scopeId) {
      const fetchMilestones = async () => {
        try {
          setBillingMilestonesLoading(true);
          setBillingMilestonesError(null);
          const response = await getScopeBillingMilestones(scopeId);
          if (response.success) {
            setBillingMilestonesData(response);
          } else {
            setBillingMilestonesError(
              new Error(
                response.message || "Failed to fetch billing milestones"
              )
            );
          }
        } catch (err) {
          setBillingMilestonesError(err);
          setBillingMilestonesData(null);
        } finally {
          setBillingMilestonesLoading(false);
        }
      };
      fetchMilestones();
    }
  };

  // Filter activities for the specific scope
  const scopeData = useMemo(() => {
    if (!activitiesData?.data?.length || !scopeId) return null;
    return activitiesData.data.find(
      (scope) => scope.scope_id === parseInt(scopeId)
    );
  }, [activitiesData, scopeId]);

  const activities = scopeData?.activities || [];
  const billingMilestones = billingMilestonesData?.billing_milestones || [];

  // Build activityMilestoneMap from API response
  const activityMilestoneMap = useMemo(() => {
    const map = {};
    billingMilestones.forEach((milestone) => {
      if (
        milestone.linked_milestones &&
        Array.isArray(milestone.linked_milestones)
      ) {
        milestone.linked_milestones.forEach((milestoneItem) => {
          const activityId = milestoneItem.id;
          if (activityId) {
            map[activityId] = milestone.group_id;
          }
        });
      }
    });
    return map;
  }, [billingMilestones]);

  // Check if payment group allows drag & drop
  const isDragDropAllowed = (groupName) => {
    const restrictedNames = ["Initial Deposit", "Warranty"];
    return !restrictedNames.some((name) =>
      groupName?.toLowerCase().includes(name.toLowerCase())
    );
  };

  // Get scope name from query params or fallback to API data
  const scopeName =
    searchParams.get("scopeName") || scopeData?.scope_name || "Scope";

  const handleBack = () => {
    const from = searchParams.get("from");
    const tabRoute =
      from === "billing-milestones" || from === "payment-groups"
        ? "billing-milestones"
        : "schedule";

    // Keep proposal_id in the path; preserve project_id / from / view query params.
    const qs = new URLSearchParams(searchParams);
    qs.delete("scopeName");
    qs.delete("from");
    const query = qs.toString();
    navigate(
      `/project/details/${id}/${tabRoute}${query ? `?${query}` : ""}`,
    );
  };

  const toggleActivitySelection = (activityId) => {
    const newSelected = new Set(selectedActivities);
    if (newSelected.has(activityId)) {
      newSelected.delete(activityId);
    } else {
      newSelected.add(activityId);
    }
    setSelectedActivities(newSelected);
  };

  const handleDragStart = (e, activityId) => {
    if (multipleDragEnabled && selectedActivities.size > 0) {
      e.dataTransfer.setData(
        "application/json",
        JSON.stringify(Array.from(selectedActivities))
      );
      e.dataTransfer.effectAllowed = "move";
    } else {
      e.dataTransfer.setData("activityId", activityId.toString());
      e.dataTransfer.effectAllowed = "move";
    }
  };

  // ---------- Touch DnD for mobile / tablet ----------
  // Mirrors the HTML5 drag flow but uses touch events because mobile
  // browsers don't fire ondragstart/ondrop on touch.
  const [touchDrag, setTouchDrag] = useState(null);
  const TOUCH_DRAG_THRESHOLD = 6;

  const performAssign = async (groupId, activityIds) => {
    if (!groupId || !activityIds?.length) return;
    const fakeEvent = {
      preventDefault: () => {},
      dataTransfer: {
        getData: (k) =>
          k === "application/json"
            ? JSON.stringify(activityIds)
            : "",
      },
    };
    await handleDrop(fakeEvent, groupId);
  };

  const onActivityTouchStart = (e, activityId) => {
    if (e.touches?.length !== 1) return;
    const t = e.touches[0];
    setTouchDrag({
      activityId,
      ids:
        multipleDragEnabled && selectedActivities.size > 0
          ? Array.from(selectedActivities)
          : [activityId],
      startX: t.clientX,
      startY: t.clientY,
      x: t.clientX,
      y: t.clientY,
      active: false,
    });
  };

  const onActivityTouchMove = (e) => {
    if (!touchDrag) return;
    const t = e.touches?.[0];
    if (!t) return;
    const dx = t.clientX - touchDrag.startX;
    const dy = t.clientY - touchDrag.startY;
    if (
      !touchDrag.active &&
      Math.sqrt(dx * dx + dy * dy) < TOUCH_DRAG_THRESHOLD
    ) {
      setTouchDrag((d) => (d ? { ...d, x: t.clientX, y: t.clientY } : d));
      return;
    }
    e.preventDefault();
    setTouchDrag((d) =>
      d ? { ...d, active: true, x: t.clientX, y: t.clientY } : d
    );
  };

  const onActivityTouchEnd = (e) => {
    if (!touchDrag) return;
    const drag = touchDrag;
    setTouchDrag(null);
    if (!drag.active) return;
    const target = document.elementFromPoint(drag.x, drag.y);
    if (!target) return;
    const dropZone = target.closest("[data-drop-group-id]");
    const groupId = dropZone?.getAttribute("data-drop-group-id");
    if (!groupId) return;
    performAssign(groupId, drag.ids);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, groupId) => {
    e.preventDefault();
    const data = e.dataTransfer.getData("application/json");
    let activityIds = [];

    if (data) {
      try {
        activityIds = JSON.parse(data);
      } catch (e) {
        console.error("Error parsing drag data:", e);
        return;
      }
    } else {
      const activityId = e.dataTransfer.getData("activityId");
      if (activityId) {
        activityIds = [parseInt(activityId)];
      }
    }

    if (activityIds.length === 0) return;

    const paymentGroup = billingMilestones.find(
      (pg) => pg.group_id === groupId
    );

    if (!paymentGroup) {
      toast.error("Payment group not found");
      return;
    }

    if (!isDragDropAllowed(paymentGroup.group_name)) {
      toast.error(
        `Cannot link activities to "${paymentGroup.group_name}" payment group`
      );
      return;
    }

    const existingMilestoneIds =
      paymentGroup.linked_milestones?.map((m) => m.id) || [];

    const updatedMilestoneIds = [
      ...new Set([...existingMilestoneIds, ...activityIds]),
    ];

    const allLinkedActivityIds = updatedMilestoneIds;
    const linkedActivities = activities.filter((activity) =>
      allLinkedActivityIds.includes(activity.id)
    );

    const endDates = linkedActivities
      .map((activity) => activity.end_date)
      .filter((date) => date);

    const maxEndDate =
      endDates.length > 0
        ? moment.max(endDates.map((date) => moment(date)))
        : null;

    try {
      setIsUpdating(true);
      const payload = {
        milestones: updatedMilestoneIds.map((id) => ({ milestoneId: id })),
        paymentGroup: {
          groupId: paymentGroup.group_id,
          groupName: paymentGroup.group_name,
          refId:
            billingMilestonesData.ref_id || billingMilestonesData.proposal_id,
          refType: paymentGroup.ref_type || "proposal",
          type: paymentGroup.type || "PAYMENT_GROUP",
          dueDate: maxEndDate ? maxEndDate.format("YYYY-MM-DD") : null,
        },
      };

      const response = await updatePaymentGroup(payload);
      toast.success("Activities linked successfully");
      setSelectedActivities(new Set());
      refetchBillingMilestones();
    } catch (error) {
      console.error("Error updating payment group:", error);
      toast.error("Failed to link activities");
    } finally {
      setIsUpdating(false);
    }
  };

  const removeActivityFromMilestone = async (activityId, groupId) => {
    const paymentGroup = billingMilestones.find(
      (pg) => pg.group_id === groupId
    );

    if (!paymentGroup) {
      toast.error("Payment group not found");
      return;
    }

    if (!isDragDropAllowed(paymentGroup.group_name)) {
      toast.error(
        `Cannot unlink activities from "${paymentGroup.group_name}" payment group`
      );
      return;
    }

    const existingMilestoneIds =
      paymentGroup.linked_milestones?.map((m) => m.id) || [];

    const updatedMilestoneIds = existingMilestoneIds.filter(
      (id) => id !== activityId
    );

    try {
      setIsUpdating(true);
      const payload = {
        milestones: updatedMilestoneIds.map((id) => ({ milestoneId: id })),
        paymentGroup: {
          groupId: paymentGroup.group_id,
          groupName: paymentGroup.group_name,
          refId:
            billingMilestonesData.ref_id || billingMilestonesData.proposal_id,
          refType: paymentGroup.ref_type || "proposal",
          type: paymentGroup.type || "PAYMENT_GROUP",
          dueDate: paymentGroup.due_date
            ? moment(paymentGroup.due_date).format("YYYY-MM-DD")
            : null,
        },
      };

      const response = await updatePaymentGroup(payload);
      toast.success("Activity unlinked successfully");
      refetchBillingMilestones();
    } catch (error) {
      console.error("Error updating payment group:", error);
      toast.error("Failed to unlink activity");
    } finally {
      setIsUpdating(false);
    }
  };

  const getTagIcon = (tag) => {
    switch (tag?.toLowerCase()) {
      case "design":
        return <NewDesignIcon />;
      case "installation":
      case "renovation":
        return <NewRenovationIcon />;
      default:
        return <NewRenovationIcon />;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenModal = (activity) => {
    // TODO: Implement modal for starting/submitting activities
    console.log("Open modal for activity:", activity.id);
  };

  const getActivitiesForMilestone = (groupId) => {
    return activities.filter(
      (activity) => activityMilestoneMap[activity.id] === groupId
    );
  };

  const getUnlinkedActivities = () => {
    return activities.filter((activity) => !activityMilestoneMap[activity.id]);
  };

  const loading = activitiesLoading || billingMilestonesLoading;
  const error = activitiesError || billingMilestonesError;

  if (loading) {
    return (
      <Box sx={componentStyles.loadingContainer}>
        <CircularProgress size={40} />
        <Typography sx={{ ...componentStyles.loadingText, ml: 2 }}>
          Loading Activities...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={componentStyles.errorContainer}>
        <Typography sx={componentStyles.errorText}>
          Error loading Activities: {error.message || "Unknown error"}
        </Typography>
      </Box>
    );
  }

  const renderActivitiesTab = () => {
    if (activities.length === 0) {
      return (
        <Box>
          <center style={{ padding: "2vmax" }}>
            No activities found for this scope
          </center>
        </Box>
      );
    }

    if (isMobile) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {activities.map((activity) => {
            const statusBg =
              milestoneStatusStyleArray.find(
                (s) => s.status === activity?.status
              )?.backgroundColor || "#FFAD5C";
            return (
              <Box
                key={activity.id}
                sx={{
                  backgroundColor: "#fff",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={
                        activity?.milestone_image?.path ||
                        "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                      }
                      alt={activity.milestone_name}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        flexShrink: 0,
                        objectFit: "cover",
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#0D0D0E",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {activity.milestone_name}
                      </Typography>
                      {activity.tags && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.25,
                          }}
                        >
                          {getTagIcon(activity.tags)}
                          <Typography
                            sx={{ fontSize: 12, color: "#949DAA" }}
                          >
                            {activity.tags}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 12, color: "#949DAA" }}>
                      Amount
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.3,
                        fontSize: 14,
                        color: "#0D0D0E",
                        fontWeight: 500,
                      }}
                    >
                      <SmallDhiramIcon />
                      {activity.total_cost
                        ? `${activity.total_cost}`
                        : "-"}
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, color: "#949DAA" }}>
                      Start Date
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#0D0D0E" }}>
                      {activity.start_date
                        ? moment(activity.start_date).format("MMM DD, YYYY")
                        : "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, color: "#949DAA" }}>
                      End Date
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#0D0D0E" }}>
                      {activity.end_date
                        ? moment(activity.end_date).format("MMM DD, YYYY")
                        : "-"}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        border: "3px solid rgb(229, 229, 229)",
                        backgroundColor: statusBg,
                      }}
                    />
                    <Typography sx={{ fontSize: 13, color: "#0D0D0E" }}>
                      {getStatusDisplayText(activity?.status)}
                    </Typography>
                  </Box>
                  {activity.dependencies?.length ? (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#64748b",
                        textAlign: "right",
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Deps:{" "}
                      {activity.dependencies.map((d) => d.name).join(", ")}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Box>
      );
    }

    return (
      <Box>
        {activities.length > 0 ? (
          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              overflow: "none !important",
              boxShadow: "none",
            }}
          >
            <Table
              sx={{
                "& thead": {
                  "& th": {
                    color: "#949DAA",
                    fontWeight: "500",
                  },
                },
                "& tbody": {
                  "& tr:hover": {
                    backgroundColor: "#f7f7f7",
                  },
                  "& tr": {
                    borderBottom: "none",
                  },
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>Activity</StyledTableCell>
                  <StyledTableCell>Type</StyledTableCell>
                  <StyledTableCell>Start Date</StyledTableCell>
                  <StyledTableCell>End Date</StyledTableCell>
                  <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
                    <strong
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      Amount (<SmallDhiramIcon />)
                    </strong>
                  </StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Dependencies</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activities.map((activity, idx) => (
                  <TableRow key={activity.id}>
                    <StyledTableCell>
                      <Box display="flex" alignItems="center">
                        <Box
                          component="img"
                          src={
                            activity?.milestone_image
                              ? activity?.milestone_image?.path
                              : "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                          }
                          alt="Activity Icon"
                          sx={{
                            width: 45,
                            height: 45,
                            borderRadius: "8px",
                            border: "2px solid white",
                            boxShadow: "0px 4px 2.67px 0px #00000026",
                            marginRight: 1,
                          }}
                        />
                        <span>{activity.milestone_name}</span>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box display="flex" alignItems="center" gap="5px">
                        <Box display="flex" alignItems="center">
                          {getTagIcon(activity.tags)}
                        </Box>
                        <Box color="#949DAA">{activity.tags}</Box>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <p
                        style={{
                          color: "#949DAA",
                          marginBottom: "2px",
                          fontWeight: "500",
                        }}
                      >
                        {moment(activity.start_date).format("MMM DD, YYYY")}
                      </p>
                    </StyledTableCell>
                    <StyledTableCell>
                      <p
                        style={{
                          color: "#949DAA",
                          marginBottom: "2px",
                          fontWeight: "500",
                        }}
                      >
                        {moment(activity.end_date).format("MMM DD, YYYY")}
                      </p>
                    </StyledTableCell>
                    <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
                      {activity.total_cost
                        ? `${formattedAmountWithoutAed(activity.total_cost)}`
                        : "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <Box
                          sx={{
                            width: "15px",
                            height: "15px",
                            borderRadius: "50%",
                            border: "3px solid rgb(229, 229, 229)",
                            backgroundColor:
                              milestoneStatusStyleArray.find(
                                (style) => style.status === activity?.status
                              )?.backgroundColor || "#FFAD5C",
                          }}
                        />
                        <Box
                          sx={{
                            fontSize: "14px",
                            color: "#0D0D0E",
                          }}
                        >
                          {getStatusDisplayText(activity?.status)}
                        </Box>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#64748b",
                        }}
                      >
                        {activity.dependencies?.length
                          ? activity.dependencies.map((d) => d.name).join(", ")
                          : "None"}
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <center style={{ padding: "2vmax", }}>No activities found for this scope</center>

        )}
      </Box>
    );
  };

  const renderBillingMilestoneTab = () => {
    const unlinkedActivities = getUnlinkedActivities();
    return (
      <Box sx={componentStyles.milestoneTabContainer}>
        <Box sx={componentStyles.milestoneGrid}>
          {/* LHS - Activities */}
          <Box sx={componentStyles.milestoneCard}>
            <Typography sx={componentStyles.milestoneCardTitle}>
              Activities ({unlinkedActivities.length})
            </Typography>
            <Box sx={componentStyles.activitiesList}>
              {unlinkedActivities.length > 0 ? (
                unlinkedActivities.map((activity) => (
                  <Box
                    key={activity.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, activity.id)}
                    onTouchStart={(e) => onActivityTouchStart(e, activity.id)}
                    onTouchMove={onActivityTouchMove}
                    onTouchEnd={onActivityTouchEnd}
                    sx={{
                      ...componentStyles.activityItem,
                      ...(selectedActivities.has(activity.id)
                        ? componentStyles.activityItemSelected
                        : componentStyles.activityItemHover),
                      touchAction:
                        touchDrag?.active && touchDrag.activityId === activity.id
                          ? "none"
                          : "auto",
                    }}
                  >
                    <Box sx={componentStyles.activityItemContent}>
                      {multipleDragEnabled && (
                        <Checkbox
                          checked={selectedActivities.has(activity.id)}
                          onChange={() => toggleActivitySelection(activity.id)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                        />
                      )}
                      <Box sx={componentStyles.activityItemDetails}>
                        <Box sx={componentStyles.activityItemInfo}>
                          <Box sx={componentStyles.activityItemHeader}>
                            {getTagIcon(activity.tags)}
                            <Typography sx={componentStyles.activityItemName}>
                              {activity.milestone_name}
                            </Typography>
                          </Box>
                          {activity.description && (
                            <Typography
                              sx={componentStyles.activityItemDescription}
                            >
                              {activity.description}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={componentStyles.activityItemDates}>
                          {activity.start_date && (
                            <p>
                              Start:{" "}
                              {moment(activity.start_date).format(
                                "DD MMM, YYYY"
                              )}
                            </p>
                          )}
                          {activity.end_date && (
                            <p>
                              End:{" "}
                              {moment(activity.end_date).format("DD MMM, YYYY")}
                            </p>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box sx={componentStyles.emptyActivitiesText}>
                  <Typography>
                    All activities are linked to milestones
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* RHS - Billing Milestones */}
          <Box sx={componentStyles.milestoneCard}>
            <Typography sx={componentStyles.milestoneCardTitle}>
              Billing Milestone ({billingMilestones.length})
            </Typography>
            <Box sx={componentStyles.activitiesList}>
              {billingMilestones.length > 0 ? (
                billingMilestones.map((milestone) => {
                  const linkedActivities = getActivitiesForMilestone(
                    milestone.group_id
                  );
                  const isRestricted = !isDragDropAllowed(milestone.group_name);
                  return (
                    <Box
                      key={milestone.group_id}
                      data-drop-group-id={
                        !isRestricted ? milestone.group_id : undefined
                      }
                      onDragOver={!isRestricted ? handleDragOver : undefined}
                      onDrop={
                        !isRestricted
                          ? (e) => handleDrop(e, milestone.group_id)
                          : undefined
                      }
                      sx={{
                        ...componentStyles.milestoneDropZone,
                        ...(isRestricted
                          ? componentStyles.milestoneDropZoneRestricted
                          : componentStyles.milestoneDropZoneActive),
                      }}
                    >
                      <Box
                        sx={
                          !isRestricted ? componentStyles.milestoneHeader : {}
                        }
                      >
                        <Box sx={componentStyles.milestoneHeaderTop}>
                          <Typography sx={componentStyles.milestoneName}>
                            {milestone.group_name}
                          </Typography>
                          <Box sx={componentStyles.milestoneMeta}>
                            {milestone.due_date && (
                              <Typography sx={componentStyles.milestoneDueDate}>
                                Due:{" "}
                                {moment(milestone.due_date).format(
                                  "DD MMM, YYYY"
                                )}
                              </Typography>
                            )}
                            {isRestricted && (
                              <Typography sx={componentStyles.restrictedBadge}>
                                Restricted
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {milestone.description && !isRestricted && (
                          <Typography sx={componentStyles.milestoneDescription}>
                            {milestone.description}
                          </Typography>
                        )}
                        <Box sx={componentStyles.milestoneInfo}>
                          {milestone.percentage && (
                            <Typography sx={{ color: "#0d0d0e", fontSize: '14px' }}>
                              Percentage: {milestone.percentage}%
                            </Typography>
                          )}
                          {milestone.milestone_amount && (
                            <Box sx={componentStyles.milestoneInfoItem}>
                              <Typography sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: "#0d0d0e", fontSize: '14px' }}>
                                Amount: <SmallDhiramIcon />
                                {formattedAmountWithoutAed(
                                  milestone.milestone_amount
                                )}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {!isRestricted && (
                        <Box sx={componentStyles.linkedActivitiesSection}>
                          <Typography
                            sx={componentStyles.linkedActivitiesLabel}
                          >
                            Linked Activities ({linkedActivities.length})
                          </Typography>
                          <Box sx={componentStyles.linkedActivitiesList}>
                            {linkedActivities.length > 0 ? (
                              linkedActivities.map((activity) => (
                                <Box
                                  key={activity.id}
                                  sx={componentStyles.linkedActivityItem}
                                >
                                  <Box sx={componentStyles.linkedActivityInfo}>
                                    {getTagIcon(activity.tags)}
                                    <Typography
                                      sx={componentStyles.linkedActivityName}
                                    >
                                      {activity.milestone_name}
                                    </Typography>
                                  </Box>
                                  <Button
                                    onClick={() =>
                                      removeActivityFromMilestone(
                                        activity.id,
                                        milestone.group_id
                                      )
                                    }
                                    disabled={isUpdating || isRestricted}
                                    sx={componentStyles.removeButton}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              ))
                            ) : (
                              <Box sx={componentStyles.dropZonePlaceholder}>
                                <Typography>Drop activities here</Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })
              ) : (
                <Box sx={componentStyles.emptyMilestonesText}>
                  <Typography>No payment groups found</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={componentStyles.container}>
      {/* Floating ghost during touch drag */}
      {touchDrag?.active && (
        <Box
          sx={{
            position: "fixed",
            left: touchDrag.x + 12,
            top: touchDrag.y + 12,
            zIndex: 2000,
            pointerEvents: "none",
            backgroundColor: "#4D6BF6",
            color: "#fff",
            fontSize: 12,
            fontWeight: 600,
            padding: "6px 10px",
            borderRadius: "999px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
          }}
        >
          {touchDrag.ids.length} activit
          {touchDrag.ids.length === 1 ? "y" : "ies"}
        </Box>
      )}

      <header style={componentStyles?.header || {}}>
        <div style={componentStyles?.headerContent || {}}>
          <LeftArrowIcon onClick={handleBack} style={{ cursor: "pointer" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <div
              style={{
                ...componentStyles?.idText,
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              {scopeName} - Activities Details
            </div>
            <div style={{ color: "#949daa", fontSize: "14px" }}>
              Manage activities and link them to billing milestones
            </div>
          </div>
        </div>
      </header>

      <div style={{ padding: "2vh 2vw" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            "& .MuiTabs-indicator": {
              background: "none",
            },
            "& .MuiTabs-scrollButtons": {
              display: "none",
            },
          }}
        >
          <StyledTab label="Activities" />
          <StyledTab label="Milestone Group" />
        </Tabs>
        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && renderActivitiesTab()}
          {activeTab === 1 && renderBillingMilestoneTab()}
        </Box>
      </div>
    </Box>
  );
};

export default ActivitiesDetails;
